/**
 * Funções HTTP do pipeline ACE-V — POST /api/analise (multipart) // ux-neutrality:allow doc-comment
 * + GET /api/analise/:id (resultado completo) + GET /:id/status (polling). // ux-neutrality:allow doc-comment
 *
 * Schemas espelham apps/api/types/forensic.py — mantidos a mão por simplicidade // ux-neutrality:allow doc-comment
 * (gerador OpenAPI -> TS fica para post-demo se a divergência crescer). // ux-neutrality:allow doc-comment
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';

// Constantes do contrato ---------------------------------------------------

export type AnalysisStatus = 'pending' | 'running' | 'completed' | 'failed';

export type CriterionStatus = 'convergent' | 'divergent' | 'neutral' | 'inconclusive';
export type CriterionWeight = 'genetic' | 'generic';
export type CriterionImpact = 'alto' | 'medio' | 'baixo';

export type LRClassification =
  | 'h1_muito_forte'
  | 'h1_forte'
  | 'h1_moderado'
  | 'h1_limitado'
  | 'h1_fraco'
  | 'inconclusivo'
  | 'h2_fraco'
  | 'h2_limitado'
  | 'h2_moderado'
  | 'h2_forte'
  | 'h2_muito_forte';

export type FraudTypology =
  | 'imitacao_livre'
  | 'imitacao_servil'
  | 'falsificacao_exercitada'
  | 'decalque_direto'
  | 'decalque_indireto'
  | 'disfarce_proprio'
  | 'imitacao_memoria'
  | 'hibridismo'
  | 'indeterminada';

export interface BoundingBoxData {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * BoundingBox top-level para render no canvas Fabric (E4.F1). // ux-neutrality:allow doc-comment
 * Difere de BoundingBoxData por exigir criterion_id (1..10 da taxonomia §3); // ux-neutrality:allow doc-comment
 * label opcional dá uma legenda curta (ex.: "calibre alto"). // ux-neutrality:allow doc-comment
 */
export interface CanvasBoundingBoxData extends BoundingBoxData {
  criterion_id: number;
  label?: string | null;
}

export interface CriterionAssessmentData {
  criterion_id: number;
  status: CriterionStatus;
  weight: CriterionWeight;
  impact: CriterionImpact;
  evidence_text: string;
  evidence_coords: BoundingBoxData[];
  references: string[];
}

export interface AnalysisResultData {
  analysis_id: string;
  criteria: CriterionAssessmentData[];
  likelihood_ratio: number;
  lr_classification: LRClassification;
  likely_fraud_typology: FraudTypology | null;
  hypothesis_h1: string;
  hypothesis_h2: string;
  self_verification_passed: boolean;
  // bounding_boxes só aparece em laudos emitidos após E4.F1 (Pydantic default=[]).
  // Análises antigas (E3.F1 round 1/2) podem ter o campo omitido — daí o opcional.
  bounding_boxes?: CanvasBoundingBoxData[];
}

export interface AnalysisRecordData {
  id: string;
  usuario_id: string;
  escritorio_id: string;
  status: AnalysisStatus;
  referencia_blob_path: string;
  questionada_blob_path: string;
  result: AnalysisResultData | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  // E4.F2a — preenchidos quando o perito chama POST /analise/:id/aprovar.
  // Opcionais para preservar compat com registros anteriores que ainda não
  // têm a coluna no schema serializado.
  approved_at?: string | null;
  approved_by_usuario_id?: string | null;
  // E4.F2a.C04 — email do perito que aprovou (resolvido pelo backend via
  // JOIN com `users`); `null` quando ainda não aprovado ou usuário removido.
  approved_by_email?: string | null;
  // E4.F2b — edição persistida das BBs feita pelo perito no canvas.
  // `null`/`undefined` => UI exibe `result.bounding_boxes` original. Quando
  // presente, sobrepõe o array do laudo até PATCH com `null` restaurar.
  bounding_boxes_override?: CanvasBoundingBoxData[] | null;
}

export interface AnalysisStatusData {
  analysis_id: string;
  status: AnalysisStatus;
  completed_at: string | null;
}

export interface AnalysisApprovalData {
  analysis_id: string;
  approved_at: string;
  approved_by_usuario_id: string;
  approved_by_email: string | null;
}

// Erros tipados ------------------------------------------------------------

export type AnalysisErrorReason =
  | 'no_api_key'
  | 'image_invalid'
  | 'image_too_large'
  | 'not_found'
  | 'validation'
  | 'already_approved'
  | 'not_completed'
  | 'network'
  | 'unknown';

export class AnalysisError extends Error {
  public readonly reason: AnalysisErrorReason;

  constructor(reason: AnalysisErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'AnalysisError';
  }
}

const HTTP_REQUEST_ENTITY_TOO_LARGE = 413;
const HTTP_UNPROCESSABLE = 422;
const HTTP_CONFLICT = 409;
const HTTP_NOT_FOUND = 404;

const IMAGE_INVALID_DETAILS = new Set([
  'ANALYSIS_IMAGE_EXTENSION_INVALID',
  'ANALYSIS_IMAGE_MIME_INVALID',
  'ANALYSIS_IMAGE_EMPTY',
]);

function isNetworkError(axiosErr: AxiosError): boolean {
  return axiosErr.code === 'ERR_NETWORK' || axiosErr.code === 'ECONNABORTED';
}

function reasonForUnprocessable(detail: string | undefined): AnalysisErrorReason {
  return detail !== undefined && IMAGE_INVALID_DETAILS.has(detail) ? 'image_invalid' : 'validation';
}

function classifyCreateError(err: unknown): AnalysisError {
  if (!axios.isAxiosError(err)) return new AnalysisError('unknown');
  const axiosErr = err as AxiosError<{ detail?: string }>;
  const detail = axiosErr.response?.data?.detail;
  const httpStatus = axiosErr.response?.status;
  if (httpStatus === HTTP_REQUEST_ENTITY_TOO_LARGE) return new AnalysisError('image_too_large');
  if (httpStatus === HTTP_UNPROCESSABLE) return new AnalysisError(reasonForUnprocessable(detail));
  if (httpStatus === HTTP_CONFLICT && detail === 'ANALYSIS_USER_HAS_NO_ESCRITORIO') {
    return new AnalysisError('no_api_key');
  }
  if (isNetworkError(axiosErr)) return new AnalysisError('network');
  return new AnalysisError('unknown');
}

function classifyReadError(err: unknown): AnalysisError {
  if (!axios.isAxiosError(err)) return new AnalysisError('unknown');
  const axiosErr = err as AxiosError;
  if (axiosErr.response?.status === HTTP_NOT_FOUND) return new AnalysisError('not_found');
  if (isNetworkError(axiosErr)) return new AnalysisError('network');
  return new AnalysisError('unknown');
}

// HTTP --------------------------------------------------------------------

/**
 * Envia o par de imagens para análise. Retorna o registro `pending` recém-criado. // ux-neutrality:allow doc-comment
 */
export async function createAnalysis(
  client: AxiosInstance,
  referencia: File,
  questionada: File,
): Promise<AnalysisStatusData> {
  const form = new FormData();
  form.append('referencia', referencia);
  form.append('questionada', questionada);
  try {
    const { data } = await client.post<AnalysisStatusData>('/analise', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (err) {
    throw classifyCreateError(err);
  }
}

export async function getAnalysis(
  client: AxiosInstance,
  analysisId: string,
): Promise<AnalysisRecordData> {
  try {
    const { data } = await client.get<AnalysisRecordData>(`/analise/${analysisId}`);
    return data;
  } catch (err) {
    throw classifyReadError(err);
  }
}

export async function getAnalysisStatus(
  client: AxiosInstance,
  analysisId: string,
): Promise<AnalysisStatusData> {
  try {
    const { data } = await client.get<AnalysisStatusData>(`/analise/${analysisId}/status`);
    return data;
  } catch (err) {
    throw classifyReadError(err);
  }
}

export type AnalysisBlobKind = 'ref' | 'quest';

/**
 * Baixa o binário de uma das imagens persistidas (referência ou questionada). // ux-neutrality:allow doc-comment
 * Resposta é Blob — o caller compõe URL.createObjectURL para exibir e revoga // ux-neutrality:allow doc-comment
 * na limpeza do efeito. JWT é injetado pelo cliente Axios; tag <img src> // ux-neutrality:allow doc-comment
 * direta não funcionaria porque navegador não envia Authorization header. // ux-neutrality:allow doc-comment
 */
function classifyApproveError(err: unknown): AnalysisError {
  if (!axios.isAxiosError(err)) return new AnalysisError('unknown');
  const axiosErr = err as AxiosError<{ detail?: string }>;
  const detail = axiosErr.response?.data?.detail;
  const httpStatus = axiosErr.response?.status;
  if (httpStatus === HTTP_NOT_FOUND) return new AnalysisError('not_found');
  if (httpStatus === HTTP_CONFLICT && detail === 'ANALYSIS_ALREADY_APPROVED') {
    return new AnalysisError('already_approved');
  }
  if (httpStatus === HTTP_UNPROCESSABLE && detail === 'ANALYSIS_NOT_COMPLETED') {
    return new AnalysisError('not_completed');
  }
  if (isNetworkError(axiosErr)) return new AnalysisError('network');
  return new AnalysisError('unknown');
}

export async function approveAnalysis(
  client: AxiosInstance,
  analysisId: string,
): Promise<AnalysisApprovalData> {
  try {
    const { data } = await client.post<AnalysisApprovalData>(`/analise/${analysisId}/aprovar`);
    return data;
  } catch (err) {
    throw classifyApproveError(err);
  }
}

function classifyBboxOverrideError(err: unknown): AnalysisError {
  if (!axios.isAxiosError(err)) return new AnalysisError('unknown');
  const axiosErr = err as AxiosError<{ detail?: string }>;
  const detail = axiosErr.response?.data?.detail;
  const httpStatus = axiosErr.response?.status;
  if (httpStatus === HTTP_NOT_FOUND) return new AnalysisError('not_found');
  if (httpStatus === HTTP_CONFLICT && detail === 'ANALYSIS_ALREADY_APPROVED') {
    return new AnalysisError('already_approved');
  }
  if (httpStatus === HTTP_UNPROCESSABLE) return new AnalysisError('validation');
  if (isNetworkError(axiosErr)) return new AnalysisError('network');
  return new AnalysisError('unknown');
}

/**
 * Persiste a edição manual das bounding boxes (E4.F2b). // ux-neutrality:allow doc-comment
 *
 * `bboxes=null` limpa o override e devolve o controle ao laudo original. // ux-neutrality:allow doc-comment
 * Idempotente: o backend faz replace-the-whole-array. Erros mapeados: // ux-neutrality:allow doc-comment
 *  - 'not_found'         (HTTP 404 — fora do tenant ou inexistente). // ux-neutrality:allow doc-comment
 *  - 'already_approved'  (HTTP 409 — análise pós-aprovação imutável). // ux-neutrality:allow doc-comment
 *  - 'validation'        (HTTP 422 — body inválido / BB sem criterion_id). // ux-neutrality:allow doc-comment
 *  - 'network'           (sem rede / timeout). // ux-neutrality:allow doc-comment
 */
export async function updateBoundingBoxes(
  client: AxiosInstance,
  analysisId: string,
  bboxes: CanvasBoundingBoxData[] | null,
): Promise<AnalysisRecordData> {
  try {
    const { data } = await client.patch<AnalysisRecordData>(
      `/analise/${analysisId}/bounding-boxes`,
      { bounding_boxes: bboxes },
    );
    return data;
  } catch (err) {
    throw classifyBboxOverrideError(err);
  }
}

export async function fetchAnalysisBlob(
  client: AxiosInstance,
  analysisId: string,
  kind: AnalysisBlobKind,
): Promise<Blob> {
  try {
    const { data } = await client.get<Blob>(`/analise/${analysisId}/blob/${kind}`, {
      responseType: 'blob',
    });
    return data;
  } catch (err) {
    throw classifyReadError(err);
  }
}
